import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  EmptyState,
  Filters,
  LoadingState,
  Pagination,
  Search,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createResourcesApi } from "../api";
import { formatLabel, resourceStatusTone } from "../labels";
import type {
  BookableResourceStatus,
  BookableResourceType,
  ResourceDto,
  VisibilityStatus,
} from "../types";
import { CreateResourceDialog } from "../components/ResourceDialogs";

const STATUSES: BookableResourceStatus[] = [
  "pending_setup",
  "available",
  "unavailable",
  "paused",
  "under_maintenance",
  "restricted",
  "retired",
];

const TYPES: BookableResourceType[] = [
  "court",
  "field",
  "studio",
  "pool_lane",
  "meeting_room",
  "rental_equipment",
  "future_governed_type",
];

const VISIBILITY: VisibilityStatus[] = ["visible", "hidden", "paused", "restricted"];

const SORT_OPTIONS = [
  { value: "name", label: "Name (A–Z)" },
  { value: "-name", label: "Name (Z–A)" },
  { value: "status", label: "Status" },
  { value: "resource_type", label: "Type" },
  { value: "id", label: "ID" },
];

export function ResourcesDirectoryPage() {
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Business Center", to: "/app/businesses" },
    { label: "Bookable Resources" },
  ]);

  const client = useApiClient();
  const api = useMemo(() => createResourcesApi(client), [client]);
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const q = searchParams.get("q") ?? "";
  const status = (searchParams.get("status") ?? "") as BookableResourceStatus | "";
  const resourceType = (searchParams.get("resource_type") ??
    "") as BookableResourceType | "";
  const visibilityStatus = (searchParams.get("visibility_status") ??
    "") as VisibilityStatus | "";
  const businessId = searchParams.get("business_id") ?? "";
  const capabilityId = searchParams.get("business_capability_id") ?? "";
  const sort = searchParams.get("sort") ?? "name";
  const cursor = searchParams.get("cursor") ?? undefined;

  const [rows, setRows] = useState<ResourceDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState(q);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const page = cursorStack.length + 1;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.list({
        ...(cursor ? { cursor } : {}),
        limit: 25,
        sort,
        ...(q ? { q } : {}),
        ...(status ? { status } : {}),
        ...(resourceType ? { resource_type: resourceType } : {}),
        ...(visibilityStatus ? { visibility_status: visibilityStatus } : {}),
        ...(businessId ? { business_id: businessId } : {}),
        ...(capabilityId ? { business_capability_id: capabilityId } : {}),
      });
      setRows(list.resources);
      setNextCursor(list.pagination.next_cursor);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRows([]);
      setNextCursor(undefined);
    } finally {
      setLoading(false);
    }
  }, [
    api,
    cursor,
    sort,
    q,
    status,
    resourceType,
    visibilityStatus,
    businessId,
    capabilityId,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSearchDraft(q);
  }, [q]);

  function updateParams(patch: Record<string, string | undefined>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next);
  }

  function applySearch() {
    setCursorStack([]);
    updateParams({ q: searchDraft.trim() || undefined, cursor: undefined });
  }

  function goNext() {
    if (!nextCursor) return;
    setCursorStack((stack) => [...stack, cursor ?? ""]);
    updateParams({ cursor: nextCursor });
  }

  function goPrevious() {
    if (cursorStack.length === 0) return;
    const stack = [...cursorStack];
    const previous = stack.pop();
    setCursorStack(stack);
    updateParams({
      cursor: previous && previous.length > 0 ? previous : undefined,
    });
  }

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Bookable Resources"
        description="Operate supply: courts, fields, rooms, and other bookable units. Availability, blackouts, maintenance, and pricing/visibility policies live on the resource."
        actions={
          <Flex gap={8}>
            <Button variant="secondary" onClick={() => setCreateOpen(true)}>
              Create resource
            </Button>
            <Button variant="secondary" onClick={() => void load()}>
              Refresh
            </Button>
          </Flex>
        }
      />

      <Filters>
        <div style={{ flex: "1 1 240px", minWidth: 200 }}>
          <Search
            value={searchDraft}
            onValueChange={setSearchDraft}
            placeholder="Search resource name"
            onKeyDown={(event) => {
              if (event.key === "Enter") applySearch();
            }}
          />
        </div>
        <Select
          label="Status"
          value={status}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              status: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All statuses" },
            ...STATUSES.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Select
          label="Type"
          value={resourceType}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              resource_type: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All types" },
            ...TYPES.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Select
          label="Visibility"
          value={visibilityStatus}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              visibility_status: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All visibility" },
            ...VISIBILITY.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Select
          label="Sort"
          value={sort}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({ sort: event.target.value, cursor: undefined });
          }}
          options={SORT_OPTIONS}
        />
        <Button variant="primary" onClick={applySearch}>
          Apply search
        </Button>
      </Filters>

      {businessId || capabilityId ? (
        <Alert tone="info" title="Filtered to a Business">
          {businessId ? `Business ${businessId}. ` : ""}
          {capabilityId ? `Capability ${capabilityId}.` : ""}
        </Alert>
      ) : null}

      {error ? (
        <Alert tone="danger" title="Unable to load Bookable Resources">
          {error}
        </Alert>
      ) : null}

      {loading ? <LoadingState message="Loading Bookable Resources…" /> : null}

      {!loading && !error && rows.length === 0 ? (
        <EmptyState
          reason={q || status || resourceType || businessId ? "filters" : "none"}
          title="No Bookable Resources found"
          action={
            <Button
              variant="secondary"
              onClick={() => {
                setCursorStack([]);
                setSearchParams(new URLSearchParams());
              }}
            >
              Clear filters
            </Button>
          }
        />
      ) : null}

      {!loading && rows.length > 0 ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                Showing {rows.length} resource{rows.length === 1 ? "" : "s"}
              </Text>
            }
            right={
              <Pagination
                page={page}
                pageCount={nextCursor ? page + 1 : page}
                onPageChange={(nextPage) => {
                  if (nextPage > page) goNext();
                  if (nextPage < page) goPrevious();
                }}
              />
            }
          />
          <Table>
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Type</Th>
                <Th>Status</Th>
                <Th>Visibility</Th>
                <Th>Capacity</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id}>
                  <Td>
                    <Text role="bodyStrong" as="div">
                      {row.name}
                    </Text>
                    <Text role="caption" color="muted" as="div">
                      {row.id}
                    </Text>
                  </Td>
                  <Td>{formatLabel(row.resource_type)}</Td>
                  <Td>
                    <StatusChip tone={resourceStatusTone(row.status)}>
                      {formatLabel(row.status)}
                    </StatusChip>
                  </Td>
                  <Td>{formatLabel(row.visibility_status)}</Td>
                  <Td>{row.capacity ?? "—"}</Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() =>
                        void navigate(`/app/businesses/resources/${row.id}`)
                      }
                    >
                      Open
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Stack>
      ) : null}

      <CreateResourceDialog
        open={createOpen}
        busy={createBusy}
        error={createError}
        {...(capabilityId ? { defaultCapabilityId: capabilityId } : {})}
        onClose={() => {
          setCreateOpen(false);
          setCreateError(null);
        }}
        onSubmit={async (input) => {
          setCreateBusy(true);
          setCreateError(null);
          try {
            const result = await api.create(input);
            toast.push({
              title: "Resource created",
              description: result.resource.name,
              tone: "success",
            });
            setCreateOpen(false);
            void navigate(`/app/businesses/resources/${result.resource.id}`);
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setCreateBusy(false);
          }
        }}
      />
    </Stack>
  );
}
